import { type ChangeEvent, type KeyboardEvent, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';

import type { SimulatorComposerState } from './channel-simulator.hooks';

export const SimulatorComposer = ({
  composer,
  placeholder,
}: {
  composer: SimulatorComposerState;
  placeholder: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      composer.send();
    }
  };
  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    composer.setAttachment(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  return (
    <YStack className="shrink-0 gap-1">
      {composer.attachment && (
        <XStack className="items-center gap-2 text-xs">
          <Icons.attachment className="size-3" />
          <span className="truncate">{composer.attachment.name}</span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Remove attachment"
            onClick={() => composer.setAttachment(null)}
          >
            <Icons.trash />
          </Button>
        </XStack>
      )}
      <XStack className="items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          aria-label="Attachment"
          onChange={onFileChange}
        />
        <Button
          variant="outline"
          size="icon"
          aria-label="Add attachment"
          onClick={() => fileInputRef.current?.click()}
        >
          <Icons.plus />
        </Button>
        <textarea
          aria-label="Message"
          value={composer.text}
          rows={2}
          placeholder={placeholder}
          onChange={(event) => composer.setText(event.target.value)}
          onKeyDown={onKeyDown}
          className="max-h-32 flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none"
        />
        <Button disabled={!composer.canSend} onClick={composer.send}>
          Send
        </Button>
      </XStack>
      {composer.error && (
        <span className="text-xs text-danger-11">
          Send failed: {composer.error.message}
        </span>
      )}
    </YStack>
  );
};
